import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@material-ui/core';
import ToggleField from 'components/FormElements/ToggleField/ToggleField';
import BipsEditor, { IBipsValue, hasBipsValidationErrors } from './BipsEditor';
import { IReferralAgreementData } from './ReferralAgreementForm';

export interface IReferralTarget {
  id: string;
  name: string;
}

interface IApmModalProps {
  agreement?: IReferralAgreementData | null;
  brands: IReferralTarget[];
  connectors: IReferralTarget[];
  onClose: () => void;
  onSave: (data: IReferralAgreementData) => Promise<void>;
}

const PERIODS: ['MONTHLY' | 'QUARTERLY' | 'ANNUALLY' | 'CUSTOM', string][] = [
  ['MONTHLY', 'Monthly'],
  ['QUARTERLY', 'Quarterly'],
  ['ANNUALLY', 'Annually'],
  ['CUSTOM', 'Custom (days)'],
];
const REF_TYPES: ['GROSS_AMOUNT' | 'MSF_PERCENT_BASIS' | 'OTHER', string][] = [
  ['GROSS_AMOUNT', 'Gross Amount'],
  ['MSF_PERCENT_BASIS', 'MSF Basis'],
  ['OTHER', 'Other'],
];
const ALLOWED_CURRENCIES = ['USD', 'AUD', 'SGD', 'EUR', 'GBP'];

const BRAND_COLORS = [
  '#1a1a2e', '#c8102e', '#006341', '#e94560',
  '#0f3460', '#533483', '#00704a', '#4a1942',
  '#2c3e50', '#16a085', '#8e44ad', '#1e40af',
];

function brandColor(name: string): string {
  if (!name) return '#6B7280';
  let idx = 0;
  for (let i = 0; i < name.length; i += 1) {
    idx = (idx + name.charCodeAt(i)) % BRAND_COLORS.length;
  }
  return BRAND_COLORS[idx];
}

function brandInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

const ApmModal: React.FC<IApmModalProps> = ({
  agreement,
  brands,
  connectors,
  onClose,
  onSave,
}) => {
  const isNew = !agreement?.id;

  const [isSaving, setIsSaving] = useState(false);
  const [targetType, setTargetType] = useState<'BRAND' | 'CONNECTOR'>(agreement?.targetType ?? 'BRAND');
  const [form, setForm] = useState<IReferralAgreementData>({
    brandId: null,
    connectorId: null,
    isActive: true,
    bips: 0,
    invoicingCurrency: 'USD',
    xeroContactId: '',
    refType: 'GROSS_AMOUNT',
    period: 'MONTHLY',
    customDays: null,
    tiered: false,
    tiers: [],
    ...agreement,
  });

  const s = (patch: Partial<IReferralAgreementData>) => setForm((f) => ({ ...f, ...patch }));

  const bipsValue: IBipsValue = {
    tiered: form.tiered ?? false,
    flatBips: form.bips ?? 0,
    tiers: (form.tiers ?? []).map((t) => ({ ...t, max: t.max ?? '' })),
  };

  const handleBipsChange = (v: IBipsValue) => {
    s({
      tiered: v.tiered,
      bips: v.flatBips,
      tiers: v.tiers.map((t) => ({ ...t, max: t.max === '' ? null : Number(t.max) })),
    });
  };

  const targets = targetType === 'BRAND' ? brands : connectors;
  const selectedTargetId = (targetType === 'BRAND' ? form.brandId : form.connectorId) ?? '';
  const targetName = isNew
    ? targets.find((t) => t.id === selectedTargetId)?.name
    : agreement?.targetName;

  const handleTargetTypeChange = (type: 'BRAND' | 'CONNECTOR') => {
    setTargetType(type);
    s({ brandId: null, connectorId: null });
  };

  const handleTargetChange = (id: string) => {
    s(targetType === 'BRAND'
      ? { brandId: id || null, connectorId: null }
      : { connectorId: id || null, brandId: null });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch {
      // error toast shown by caller
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      aria-labelledby="apm-modal-title"
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open
    >
      <DialogTitle id="apm-modal-title">
        <div className="apm-modal-title">
          {!isNew && targetName && (
            <span
              className="apm-brand-badge"
              style={{ background: brandColor(targetName) }}
            >
              {brandInitials(targetName)}
            </span>
          )}
          <div>
            <div className="apm-modal-name">
              {isNew ? 'New Referral Agreement' : `${targetName ?? ''} Settings`}
            </div>
            <div className="apm-modal-sub">
              {targetType === 'CONNECTOR' ? 'Connector-level' : 'Brand-level'}
              {' '}
              referral configuration
            </div>
          </div>
        </div>
      </DialogTitle>

      <DialogContent>
        {isNew && (
          <>
            <div className="apm-modal-field">
              <div className="apm-modal-label">Agreement Applies To</div>
              <div className="apm-modal-ref-types">
                {([['BRAND', 'Brand'], ['CONNECTOR', 'Connector']] as const).map(([v, l]) => (
                  <label
                    className={`apm-modal-ref-option${targetType === v ? ' selected' : ''}`}
                    htmlFor={`targetType-${v}`}
                    key={v}
                  >
                    <input
                      checked={targetType === v}
                      id={`targetType-${v}`}
                      name="targetType"
                      onChange={() => handleTargetTypeChange(v)}
                      style={{ accentColor: '#3A39FF' }}
                      type="radio"
                      value={v}
                    />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
              <div className="apm-modal-info-box apm-modal-info-box--sm">
                {targetType === 'BRAND'
                  ? 'For brands we integrate with directly. Covers all transactions for the brand.'
                  : 'For integrations through a connector. Covers all transactions through the connector, across all its brands.'}
              </div>
            </div>

            <div className="apm-modal-field">
              <div className="apm-modal-label">{targetType === 'BRAND' ? 'Brand' : 'Connector'}</div>
              <select
                aria-label={targetType === 'BRAND' ? 'Brand' : 'Connector'}
                className="apm-modal-select"
                onChange={(e) => handleTargetChange(e.target.value)}
                required
                value={selectedTargetId}
              >
                <option value="">{`Select a ${targetType === 'BRAND' ? 'brand' : 'connector'}…`}</option>
                {targets.map((t) => (
                  <option
                    key={t.id}
                    value={t.id}
                  >
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="apm-modal-toggle-row">
          <div>
            <div className="apm-modal-toggle-title">Referral Active</div>
            <div className="apm-modal-toggle-sub">
              Enable referral invoicing for
              {' '}
              {targetName ?? `this ${targetType === 'BRAND' ? 'brand' : 'connector'}`}
            </div>
          </div>
          <ToggleField
            checked={form.isActive}
            id="apm-active"
            labelText=""
            name="isActive"
            onChange={(e) => s({ isActive: (e.target as HTMLInputElement).checked })}
          />
        </div>

        <div className="apm-modal-grid-2">
          <div className="apm-modal-field">
            <div className="apm-modal-label">Invoice Period</div>
            <select
              aria-label="Invoice period"
              className="apm-modal-select"
              onChange={(e) => s({ period: e.target.value as IReferralAgreementData['period'] })}
              value={form.period ?? 'MONTHLY'}
            >
              {PERIODS.map(([v, l]) => (
                <option
                  key={v}
                  value={v}
                >
                  {l}
                </option>
              ))}
            </select>
          </div>

          {form.period === 'CUSTOM' && (
            <div className="apm-modal-field">
              <div className="apm-modal-label">Number of Days</div>
              <input
                aria-label="Custom invoice period days"
                className="apm-modal-input"
                min={1}
                onChange={(e) => s({ customDays: Number(e.target.value) })}
                type="number"
                value={form.customDays ?? ''}
              />
            </div>
          )}
        </div>

        <div className="apm-modal-field">
          <div className="apm-modal-label-row">
            <span className="apm-modal-label">Referral Type</span>
            <span className="apm-tooltip-wrap">
              <span className="apm-modal-info-icon">?</span>
              <span className="apm-tooltip-box">
                Only GROSS_AMOUNT generates invoices. MSF and OTHER types do not trigger invoice generation.
              </span>
            </span>
          </div>
          <div className="apm-modal-ref-types">
            {REF_TYPES.map(([v, l]) => (
              <label
                className={`apm-modal-ref-option${form.refType === v ? ' selected' : ''}`}
                htmlFor={`refType-${v}`}
                key={v}
              >
                <input
                  checked={form.refType === v}
                  id={`refType-${v}`}
                  name="refType"
                  onChange={() => s({ refType: v })}
                  style={{ accentColor: '#3A39FF' }}
                  type="radio"
                  value={v}
                />
                <span>{l}</span>
              </label>
            ))}
          </div>
          {form.refType !== 'GROSS_AMOUNT' && (
            <div className="apm-modal-info-box">
              No invoice will be generated — only Gross Amount generates invoices.
            </div>
          )}
        </div>

        <div className="apm-modal-field">
          <div className="apm-modal-label">Invoicing Currency</div>
          <select
            aria-label="Invoicing currency"
            className="apm-modal-select"
            onChange={(e) => s({ invoicingCurrency: e.target.value })}
            value={form.invoicingCurrency}
          >
            {ALLOWED_CURRENCIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>

        <hr className="apm-modal-hr" />

        <div className="apm-modal-field">
          <div className="apm-modal-bips-title">Default BiPs Rate (Agreement Fallback)</div>
          <div className="apm-modal-info-box apm-modal-info-box--sm">
            This rate applies when no entity-level override exists for a merchant.
          </div>
          <BipsEditor
            invoicingCurrency={form.invoicingCurrency}
            onChange={handleBipsChange}
            value={bipsValue}
          />
        </div>

        {isNew && (
          <div className="apm-modal-field">
            <div className="apm-modal-label">Xero Contact ID (Optional)</div>
            <input
              aria-label="Xero contact ID"
              className="apm-modal-input"
              onChange={(e) => s({ xeroContactId: e.target.value })}
              value={form.xeroContactId ?? ''}
            />
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <button
          className="btn btn-outline"
          onClick={onClose}
          type="button"
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={isSaving || (isNew && !selectedTargetId) || hasBipsValidationErrors(bipsValue)}
          onClick={handleSave}
          type="button"
        >
          {isSaving ? (
            <>
              <CircularProgress
                size={16}
                style={{ color: '#fff', marginRight: 6 }}
              />
              Saving...
            </>
          ) : 'Save Changes'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default ApmModal;
